import React, { useMemo, useState } from 'react';
import { LuChartColumn, LuHash, LuMessagesSquare, LuPaperclip, LuThumbsUp, LuUsers } from 'react-icons/lu';
import { channelLabel } from '../../utils/sources.js';
import { Card, CardHeader } from '../ui/Card';
import AdminBadge from '../ui/AdminBadge';
import Avatar from '../ui/Avatar';
import Button from '../ui/Button';
import StatTile from '../ui/StatTile';
import { Modal } from '../ui/Modal';

const TOP = 6;

/** Ranked row: rank, name, share bar (vs the leader), message/file/reaction counts. */
const RankRow = ({ rank, name, sub, posts, uploads, reactions, max }) => (
  <li className="flex items-center gap-3 px-4 py-2.5 sm:px-5">
    <span className="w-5 flex-shrink-0 text-right text-xs font-semibold tabular-nums text-gray-400">{rank}</span>
    <Avatar text={name} size="sm" round />
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-sm font-medium text-gray-900 dark:text-white" title={name}>{name}</span>
        <span className="flex flex-shrink-0 items-center gap-3 text-xs tabular-nums text-gray-500 dark:text-gray-400">
          <span className="inline-flex items-center gap-1" title="Messages"><LuMessagesSquare className="h-3.5 w-3.5" aria-hidden />{posts}</span>
          <span className="inline-flex items-center gap-1" title="Fichiers"><LuPaperclip className="h-3.5 w-3.5" aria-hidden />{uploads}</span>
          {reactions !== undefined && <span className="hidden items-center gap-1 sm:inline-flex" title="Réactions"><LuThumbsUp className="h-3.5 w-3.5" aria-hidden />{reactions}</span>}
        </span>
      </div>
      {sub && <p className="truncate text-[11px] text-gray-400 dark:text-gray-500">{sub}</p>}
      <div className="mt-1.5 h-1.5 w-full rounded-full bg-gray-100 dark:bg-white/5" aria-hidden>
        <div className="h-1.5 rounded-full bg-brand-500" style={{ width: `${max > 0 ? Math.max(4, (posts / max) * 100) : 0}%` }} />
      </div>
    </div>
  </li>
);

const RankCard = ({ icon, title, rows, onShowAll }) => {
  const max = rows[0]?.posts || 0;
  return (
    <Card className="flex flex-col">
      <CardHeader
        icon={icon}
        title={title}
        badge={<span className="text-xs font-normal text-gray-400">{rows.length}</span>}
        actions={rows.length > TOP && <Button size="sm" variant="ghost" onClick={onShowAll}>Voir tout</Button>}
      />
      {rows.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Aucune activité.</p>
      ) : (
        <ol className="divide-y divide-gray-100 py-1 dark:divide-gray-900">
          {rows.slice(0, TOP).map((row, index) => <RankRow key={row.key} rank={index + 1} max={max} {...row} />)}
        </ol>
      )}
    </Card>
  );
};

export const WeeklyStatistics = ({ statistics, client }) => {
  const [modal, setModal] = useState(null);
  const showChannels = client?.source_type !== 'band';

  const users = useMemo(() => {
    const merged = {};
    Object.entries(statistics?.workspaces || {}).forEach(([workspaceName, workspace]) => {
      Object.entries(workspace?.users || {}).forEach(([userId, user]) => {
        if (merged[userId]) {
          merged[userId].posts += user.posts;
          merged[userId].reactions += user.reactions;
          merged[userId].uploads += user.uploads;
        } else {
          merged[userId] = { ...user, workspace: workspaceName };
        }
      });
    });
    return Object.entries(merged)
      .map(([userId, u]) => ({ key: userId, name: u.username || 'Inconnu', posts: u.posts || 0, uploads: u.uploads || 0, reactions: u.reactions || 0 }))
      .sort((a, b) => b.posts - a.posts);
  }, [statistics]);

  const channels = useMemo(() => {
    const list = [];
    Object.entries(statistics?.workspaces || {}).forEach(([workspaceName, workspace]) => {
      Object.entries(workspace?.channels || {}).forEach(([channelName, channel]) => {
        list.push({
          key: `${workspaceName}/${channelName}`,
          name: `#${channelLabel(channelName)}`,
          sub: Object.keys(statistics.workspaces).length > 1 ? workspaceName : null,
          posts: channel.posts || 0,
          uploads: channel.uploads || 0,
          reactions: channel.reactions || 0,
        });
      });
    });
    return list.sort((a, b) => b.posts - a.posts);
  }, [statistics]);

  if (!statistics) return null;

  const modalRows = modal === 'users' ? users : channels;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          icon={LuChartColumn}
          title="Statistiques de la semaine"
          subtitle="Activité des équipes sur les canaux suivis"
          badge={<AdminBadge />}
        />
        <div className="grid grid-cols-2 gap-3 p-4 sm:p-5 lg:grid-cols-4">
          <StatTile label="Contributeurs" value={statistics.totalUsers} icon={LuUsers} description="Personnes ayant publié au moins un message." />
          <StatTile label="Messages" value={statistics.totalPosts} icon={LuMessagesSquare} description="Messages publiés sur la semaine, tous canaux confondus." />
          <StatTile label="Réactions" value={statistics.totalReactions} icon={LuThumbsUp} description="Somme des réactions (emojis) reçues par les messages." />
          <StatTile label="Fichiers" value={statistics.totalUploads} icon={LuPaperclip} description="Photos et fichiers joints aux messages." />
        </div>
      </Card>

      <div className={showChannels ? 'grid gap-4 lg:grid-cols-2' : ''}>
        <RankCard icon={LuUsers} title="Top contributeurs" rows={users} onShowAll={() => setModal('users')} />
        {showChannels && <RankCard icon={LuHash} title="Top canaux" rows={channels} onShowAll={() => setModal('channels')} />}
      </div>

      <Modal
        isOpen={modal !== null}
        onClose={() => setModal(null)}
        icon={modal === 'users' ? LuUsers : LuHash}
        title={modal === 'users' ? 'Tous les contributeurs' : 'Tous les canaux'}
        subtitle={`${modalRows.length} au total, classés par nombre de messages`}
        size="md"
        bodyClassName="!px-0 !py-1"
      >
        <ol className="divide-y divide-gray-100 dark:divide-gray-900">
          {modalRows.map((row, index) => <RankRow key={row.key} rank={index + 1} max={modalRows[0]?.posts || 0} {...row} />)}
        </ol>
      </Modal>
    </div>
  );
};

export default WeeklyStatistics;
